import React from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { propertyData, brandAssets } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#0a0c10] border-t border-white/10" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Estate Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-4 mb-6">
              <img 
                src={brandAssets.wCrest} 
                alt="William Lane Estate" 
                className="w-16 h-16 object-contain"
              />
              <div>
                <p className="text-white font-serif text-lg">{propertyData.brandName}</p>
                <p className="text-amber-400/80 text-sm">{propertyData.brandTagline}</p>
              </div>
            </div>
            <p className="text-white/60 font-light leading-relaxed mb-6 max-w-md">
              A private 20-acre gated estate offering land, privacy, infrastructure, 
              and multi-generational potential — within reach of Orlando and two major airports.
            </p>
            <div className="flex items-center space-x-2 text-white/60">
              <MapPin className="w-4 h-4 text-amber-400" />
              <span className="text-sm">{propertyData.address}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Explore</h4>
            <ul className="space-y-3">
              {[
                { id: 'overview', label: 'Estate Overview' },
                { id: 'estate', label: 'The Estate' },
                { id: 'infrastructure', label: 'Infrastructure' },
                { id: 'market-evidence', label: 'Market Evidence' },
                { id: 'presentation', label: 'Presentation' },
                { id: 'virtual-tour', label: 'Virtual Tour' },
                { id: 'gallery', label: 'Photo Gallery' },
                { id: 'inquire', label: 'Private Inquiry' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-white/60 hover:text-amber-400 transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Direct Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6">Direct Inquiry</h4>
            <div className="space-y-4">
              <div>
                <p className="text-white/40 text-xs mb-1">Estate Email</p>
                <a
                  href={`mailto:${propertyData.estateContact.email}`}
                  className="text-amber-400 hover:text-amber-300 transition-colors text-sm"
                >
                  {propertyData.estateContact.email}
                </a>
              </div>
              
              <div>
                <p className="text-white/40 text-xs mb-1">Estate Website</p>
                <a
                  href={`https://${propertyData.estateContact.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-white/60 hover:text-amber-400 transition-colors text-sm"
                >
                  {propertyData.estateContact.website}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>

              <div className="pt-4">
                <p className="text-white/40 text-xs mb-2">Inquiry Types</p>
                <ul className="space-y-1">
                  {propertyData.estateContact.inquiryTypes.slice(0, 3).map((type, i) => (
                    <li key={i} className="text-white/60 text-xs">• {type}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <p className="text-white/40 text-sm">
                © {currentYear} Romeo-Brottem Family Executor. All rights reserved.
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <span className="text-white/40 text-xs">Offered at</span>
              <span className="text-amber-400 font-bold text-lg">{propertyData.priceShort}</span>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-6 pt-6 border-t border-white/5">
            <p className="text-white/30 text-xs leading-relaxed">
              All information is deemed reliable but not guaranteed and should be independently verified 
              through personal inspection by appropriate professionals. This is a direct estate marketing 
              presentation. All communications go directly to the estate, not through a third-party agent 
              or brokerage.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
